/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, PrefetchLink, Text, type TextProps } from '@/ui';
import type { APIImageType, AlignmentOptions } from '@types';
import { clsx } from 'clsx';

import { RichText } from '../rich-text';
import { CardImage } from './card-image';
import { CardTitle } from './card-title';

export interface CardProps extends React.ComponentProps<'div'> {
  /**
   * Defines the layout of the card. Horizontal layout collapses to vertical below 640px.
   * @default "horizontal"
   */
  layout?: 'horizontal' | 'vertical' | 'compact' | 'dialog';
  /**
   * Icon to display in the card. It can be an SVG string or a React node.
   */
  icon?: { svg: string | React.ReactNode };
  /**
   * URL the card will link to, if provided.
   */
  href?: string;
  /**
   * Title to display for the section.
   */
  sectionTitle?: string;
  /**
   * If true, the section title is displayed in a loading state.
   */
  sectionTitleLoading?: boolean;
  /**
   * Title to display on the card.
   */
  title?: string;
  /**
   * Defines the alignment of the content inside the card.
   */
  contentAlignment?: AlignmentOptions;
  /**
   * HTML tag to use for the title element.
   */
  titleTag?: TextProps['element'];
  /**
   * Subtitle to display below the card title.
   */
  subtitle?: string;
  /**
   * If true, the subtitle is displayed in a loading state.
   */
  subtitleLoading?: boolean;
  /**
   * If true, the card content will be reversed.
   */
  reverse?: boolean;
  /**
   * Description text to display within the card.
   */
  description?: string;
  /**
   * Image to display as a decoration for the subtitle.
   */
  subtitleDecoration?: APIImageType | Array<APIImageType>;
  /**
   * Aspect ratio settings for the card image, for both desktop and mobile.
   */
  ratio?: Partial<{
    desktop: string;
    mobile: string;
  }>;
  /**
   * Custom CSS class names for different parts of the card, such as image, content, title, subtitle, description, and actions.
   */
  classNames?: Partial<{
    image: string;
    content: string;
    title: string;
    subTitle: string;
    description: string;
    actions: string;
  }>;
  /**
   * Actions to display within the card. Accepts primary and secondary action buttons, each with props similar to the Button component.
   */
  actions?: Partial<{
    primary: React.ComponentProps<typeof Button>;
    secondary: React.ComponentProps<typeof Button>;
  }>;
  /**
   * Image or images to display in the card. Accepts an object with src and blurDataUrl (base64).
   * @fieldType image
   */
  image?: APIImageType | Array<APIImageType> | undefined;
  /**
   * Defines how the image should fit within the card, either covering or containing the space.
   * @default "object-cover"
   */
  imageFit?: 'object-cover' | 'object-contain';
  /**
   * If true, the aspect ratio of the image will be preserved.
   */
  preserveAspectRatio?: boolean;
}

export function Card({
  layout = 'horizontal',
  imageFit = 'object-cover',
  title,
  icon,
  titleTag,
  sectionTitle,
  subtitle,
  href,
  description,
  actions,
  subtitleDecoration,
  reverse,
  className,
  preserveAspectRatio,
  children,
  image,
  ratio,
  classNames,
  contentAlignment,
  id,
  ...rest
}: CardProps) {
  const Wrapper = href ? PrefetchLink : 'div';

  const titleProps = {
    icon,
    title,
    titleTag,
    contentAlignment,
    layout,
    subtitle,
    subtitleDecoration,
    href,
  };

  return (
    <section
      id={id}
      className={clsx(
        'group/card @container w-full gap-4',
        {
          'flex max-w-(--breakpoint-xl) flex-col px-4 sm:flex-row sm:gap-12 sm:px-8': layout === 'horizontal',
          'flex min-h-max max-w-(--breakpoint-md) flex-col': layout === 'vertical',
          'flex flex-col': layout === 'compact',
          'border-border sm:bg-card sm:text-card-foreground z-10 max-w-3xl grid-flow-col gap-4 place-self-center rounded-lg p-4 py-10 sm:border sm:p-12':
            layout === 'dialog',
        },
        {
          'items-center': contentAlignment?.alignment?.verticalAlignment === 'middle',
          'items-end': contentAlignment?.alignment?.verticalAlignment === 'bottom',
          'items-start': contentAlignment?.alignment?.verticalAlignment === 'top',
          'items-stretch': !contentAlignment?.alignment?.verticalAlignment,
        },
        className,
      )}
      data-component="card"
      {...rest}
    >
      {/* Large section heading */}
      {sectionTitle && (
        <Text className="flex sm:hidden" element={'h2'} elementStyle={'h3'}>
          {sectionTitle}
        </Text>
      )}

      {/* Card image and compact title overlay */}
      <CardImage
        {...{
          title,
          image,
          Wrapper,
          layout,
          href,
          ratio,
          classNames,
          preserveAspectRatio,
          imageFit,
        }}
      >
        {layout === 'compact' && (
          <CardTitle {...titleProps}>
            {/* Include the description inside the title wrapper on compact cards so that it goes inside the overlay */}
            {description && (
              <RichText className={clsx(classNames?.description, 'text-foreground! leading-snug')}>
                {description}
              </RichText>
            )}
          </CardTitle>
        )}
      </CardImage>

      {/* Non-compact content section */}
      {layout !== 'compact' && (
        <div
          className={clsx(
            'flex w-full flex-col',
            {
              'sm:gap-4': layout === 'horizontal',
              'sm:py-8': layout === 'horizontal' && !contentAlignment?.alignment?.verticalAlignment,
              'order-3 sm:order-1': layout === 'horizontal' && reverse,
              'order-3': !reverse,
            },
            classNames?.content,
          )}
        >
          {sectionTitle && (
            <Text className="hidden sm:flex" element={'h2'} elementStyle={'h3'}>
              {sectionTitle}
            </Text>
          )}

          {/* Title & Description */}
          <div
            className={clsx('flex grow flex-col justify-center gap-1', {
              'items-center text-center': contentAlignment?.alignment?.horizontalAlignment === 'center',
              'items-end text-end': contentAlignment?.alignment?.horizontalAlignment === 'right',
              'items-start text-start':
                contentAlignment?.alignment?.horizontalAlignment === 'left' ||
                !contentAlignment?.alignment?.horizontalAlignment,
            })}
          >
            <Wrapper className="w-full" {...((href ? { href } : {}) as any)}>
              <CardTitle {...titleProps} />
            </Wrapper>

            {description && (
              <div className={clsx(layout === 'vertical' && 'grow')}>
                <RichText className={classNames?.description}>{description}</RichText>
              </div>
            )}

            {children && <div className="flex w-full flex-col gap-3 pt-0.5">{children}</div>}

            {/* Actions */}
            {(actions?.primary || actions?.secondary) && (
              <div
                className={clsx(
                  'flex w-full flex-col-reverse flex-wrap gap-3 pt-4 @xs:flex-row',
                  {
                    'justify-center': contentAlignment?.alignment?.horizontalAlignment === 'center',
                    'justify-end': contentAlignment?.alignment?.horizontalAlignment === 'right',
                  },
                  classNames?.actions,
                )}
              >
                {actions?.secondary?.children && (
                  <Button
                    size="lg"
                    {...actions?.secondary}
                    variant={'outline'}
                    className={clsx('w-full shrink-0 @xs:w-auto', actions.secondary.className)}
                  />
                )}
                {actions?.primary?.children && (
                  <Button
                    size={'lg'}
                    {...actions?.primary}
                    className={clsx('w-full shrink-0 @xs:w-auto', actions.primary.className)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
